/**
 * Integration Execution Monitor
 * Real-time monitoring of integration execution and agent orchestration
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Activity,
  Database,
  TrendingUp,
  Zap,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  FileText
} from 'lucide-react';
import {
  IntegrationConfig,
  IntegrationExecution,
  AgentTask,
  executeIntegration,
  listExecutions,
  getExecutionLogs,
  formatDuration,
  calculateSuccessRate,
  getStatusColor,
  IntegrationLog,
  getLogLevelColor
} from '@/services/api/integrations';
import { AgentOrchestrationView } from './AgentOrchestrationView';
import { ExecutionLogsViewer } from './ExecutionLogsViewer';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface Props {
  integration: IntegrationConfig;
  onClose: () => void;
}

export const IntegrationExecutionMonitor: React.FC<Props> = ({ integration, onClose }) => {
  const { t } = useAppTranslation();
  const [executions, setExecutions] = useState<IntegrationExecution[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<IntegrationExecution | null>(null);
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState<'orchestration' | 'logs' | 'stats'>('orchestration');

  // Fetch executions
  const fetchExecutions = async () => {
    try {
      const data = await listExecutions(integration.id!);
      setExecutions(data);

      // Auto-select first execution if none selected
      if (!selectedExecution && data.length > 0) {
        setSelectedExecution(data[0]);
      }

      // Update selected execution if it's in the list
      if (selectedExecution) {
        const updated = data.find(e => e.id === selectedExecution.id);
        if (updated) {
          setSelectedExecution(updated);
        }
      }
    } catch (err) {
      console.error('Error fetching executions:', err);
    }
  };

  // Fetch logs for selected execution
  const fetchLogs = async () => {
    if (!selectedExecution) return;

    try {
      const data = await getExecutionLogs(integration.id!, selectedExecution.id);
      setLogs(data);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  // Execute integration
  const handleExecute = async () => {
    setExecuting(true);

    try {
      const result = await executeIntegration(integration.id!, { execution_mode: 'manual' });
      await fetchExecutions();

      // Select the new execution
      const newExecution = executions.find(e => e.id === result.execution_id);
      if (newExecution) {
        setSelectedExecution(newExecution);
      }
    } catch (err: any) {
      console.error('Error executing integration:', err);
      alert(err.response?.data?.detail || 'Failed to execute integration');
    } finally {
      setExecuting(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchExecutions();
  }, [integration.id]);

  // Fetch logs when execution changes
  useEffect(() => {
    if (selectedExecution) {
      fetchLogs();
    }
  }, [selectedExecution?.id]);

  // Auto-refresh while execution is running
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (selectedExecution?.execution_status === 'running' || selectedExecution?.execution_status === 'pending') {
        fetchExecutions();
        fetchLogs();
      }
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, selectedExecution]);

  const isRunning = selectedExecution?.execution_status === 'running' || selectedExecution?.execution_status === 'pending';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="h-6 w-6 text-teal-600" />
              {integration.name}
            </h2>
            <p className="text-sm text-gray-600 mt-1">{t('integrations.execution_monitor_agent_orchestration', 'Execution Monitor & Agent Orchestration')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                autoRefresh
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExecute}
              disabled={executing || isRunning}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 transition-colors"
            >
              {executing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {t('integrations.starting', 'Starting...')}
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  {t('integrations.execute', 'Execute')}
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Execution History Sidebar */}
          <div className="w-72 border-r border-gray-200 flex flex-col">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-sm">{t('integrations.execution_history', 'Execution History')}</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {executions.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-gray-500 text-sm">{t('integrations.no_executions_yet', 'No executions yet')}</p>
                </div>
              ) : (
                executions.map(execution => (
                  <ExecutionListItem
                    key={execution.id}
                    execution={execution}
                    selected={selectedExecution?.id === execution.id}
                    onClick={() => setSelectedExecution(execution)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {selectedExecution ? (
              <>
                {/* Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                  <TabButton
                    label={t('integrations.agent_orchestration', 'Agent Orchestration')}
                    icon={<Zap className="h-4 w-4" />}
                    active={activeTab === 'orchestration'}
                    onClick={() => setActiveTab('orchestration')}
                  />
                  <TabButton
                    label={t('integrations.logs', 'Logs')}
                    icon={<FileText className="h-4 w-4" />}
                    active={activeTab === 'logs'}
                    onClick={() => setActiveTab('logs')}
                    badge={logs.filter(l => l.log_level === 'ERROR').length || undefined}
                  />
                  <TabButton
                    label={t('integrations.statistics', 'Statistics')}
                    icon={<TrendingUp className="h-4 w-4" />}
                    active={activeTab === 'stats'}
                    onClick={() => setActiveTab('stats')}
                  />
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto">
                  {activeTab === 'orchestration' && (
                    <AgentOrchestrationView
                      execution={selectedExecution}
                      isRunning={isRunning}
                    />
                  )}

                  {activeTab === 'logs' && (
                    <ExecutionLogsViewer
                      logs={logs}
                      onRefresh={fetchLogs}
                    />
                  )}

                  {activeTab === 'stats' && (
                    <ExecutionStatsView execution={selectedExecution} />
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Database className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('integrations.select_an_execution_to_view', 'Select an execution to view details')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Execution List Item
interface ExecutionListItemProps {
  execution: IntegrationExecution;
  selected: boolean;
  onClick: () => void;
}

const ExecutionListItem: React.FC<ExecutionListItemProps> = ({ execution, selected, onClick }) => {
  const { fmt } = useFormattedDate();
  const statusIcons = {
    pending: <Clock className="h-4 w-4" />,
    running: <Activity className="h-4 w-4 animate-pulse" />,
    completed: <CheckCircle className="h-4 w-4" />,
    failed: <XCircle className="h-4 w-4" />,
    partial: <AlertTriangle className="h-4 w-4" />,
    cancelled: <X className="h-4 w-4" />
  };

  return (
    <button
      onClick={onClick}
      className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        selected ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(execution.execution_status)}`}>
          <span className="flex items-center gap-1">
            {statusIcons[execution.execution_status]}
            {execution.execution_status}
          </span>
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatDuration(execution.duration_seconds)}
        </span>
      </div>

      <p className="text-sm text-gray-900 font-medium mb-1">
        Execution #{execution.id}
      </p>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {execution.started_at
          ? fmt.dateTime(execution.started_at)
          : 'Not started'}
      </p>

      {execution.records_processed > 0 && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          {execution.records_inserted + execution.records_updated} / {execution.records_processed} records
        </div>
      )}
    </button>
  );
};

// Tab Button
interface TabButtonProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  badge?: number;
}

const TabButton: React.FC<TabButtonProps> = ({ label, icon, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 font-medium text-sm flex items-center gap-2 transition-colors ${
      active
        ? 'text-teal-600 border-b-2 border-teal-600 bg-white'
        : 'text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}
    {label}
    {badge !== undefined && badge > 0 && (
      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
        {badge}
      </span>
    )}
  </button>
);

// Execution Stats View
interface ExecutionStatsViewProps {
  execution: IntegrationExecution;
}

const ExecutionStatsView: React.FC<ExecutionStatsViewProps> = ({ execution }) => {
  const { t } = useAppTranslation();
  const successRate = calculateSuccessRate(execution);

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('integrations.execution_statistics', 'Execution Statistics')}</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label={t('integrations.records_fetched', 'Records Fetched')} value={execution.records_fetched} color="blue" />
        <StatCard label={t('integrations.records_processed', 'Records Processed')} value={execution.records_processed} color="indigo" />
        <StatCard label={t('integrations.records_inserted', 'Records Inserted')} value={execution.records_inserted} color="green" />
        <StatCard label={t('integrations.records_updated', 'Records Updated')} value={execution.records_updated} color="teal" />
        <StatCard label={t('integrations.records_failed', 'Records Failed')} value={execution.records_failed} color="red" />
        <StatCard label={t('integrations.records_skipped', 'Records Skipped')} value={execution.records_skipped} color="yellow" />
        <StatCard label={t('integrations.success_rate', 'Success Rate')} value={`${successRate}%`} color="green" />
        <StatCard label={t('integrations.duration', 'Duration')} value={formatDuration(execution.duration_seconds)} color="purple" />
      </div>

      {execution.result_summary && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">{t('integrations.summary', 'Summary')}</h4>
          <div className="space-y-2 text-sm">
            {execution.result_summary.data_quality_score && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">{t('integrations.data_quality_score', 'Data Quality Score:')}</span>
                <span className="font-medium">{execution.result_summary.data_quality_score}</span>
              </div>
            )}
            {execution.result_summary.warnings && execution.result_summary.warnings.length > 0 && (
              <div>
                <span className="text-gray-600 dark:text-gray-300">{t('integrations.warnings', 'Warnings:')}</span>
                <ul className="mt-1 space-y-1">
                  {execution.result_summary.warnings.map((warning, i) => (
                    <li key={i} className="flex items-start gap-2 text-yellow-700">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {execution.error_message && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-900">{t('integrations.error', 'Error')}</p>
              <p className="text-sm text-red-700 mt-1">{execution.error_message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  label: string;
  value: string | number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color }) => {
  const colors: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50',
    indigo: 'border-indigo-200 bg-indigo-50',
    green: 'border-green-200 bg-green-50',
    teal: 'border-teal-200 bg-teal-50',
    red: 'border-red-200 bg-red-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    purple: 'border-purple-200 bg-purple-50',
  };

  return (
    <div className={`border rounded-lg p-4 ${colors[color]}`}>
      <p className="text-xs text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
};
